import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createVNode as _createVNode, normalizeClass as _normalizeClass, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "container mx-auto px-3 py-6 sm:px-4 sm:py-8" }
const _hoisted_2 = { class: "flex items-center justify-end mb-4" }
const _hoisted_3 = { class: "flex items-center space-x-2 bg-gray-100 dark:bg-gray-800 rounded-lg p-1" }

import { ref, computed, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import WallpaperGrid from '../components/WallpaperGrid.vue';
import CategoryFilter from '../components/CategoryFilter.vue';
import GoogleAd from '../components/GoogleAd.vue';
import { wallpaperService, type Wallpaper } from '../firebase/services/wallpaperService';
import { userService } from '../firebase/services/userService';
import { useAuth } from '../composables/useAuth';
import type { DocumentSnapshot } from 'firebase/firestore';


export default /*@__PURE__*/_defineComponent({
  __name: 'Categories',
  setup(__props) {

const router = useRouter();

// State
const viewMode = ref('grid'); // 'grid' or 'preview'
const categories = ref<string[]>([]);
const selectedCategory = ref('All');
const wallpapers = ref<Wallpaper[]>([]);
const loading = ref(false);
const hasMore = ref(true);
const userFavorites = ref<string[]>([]);
const lastDoc = ref<DocumentSnapshot | null>(null);

const { currentUser } = useAuth();

// Computed
const filteredWallpapers = computed(() => {
  if (selectedCategory.value === 'All') {
    return wallpapers.value;
  }
  return wallpapers.value.filter(wallpaper => 
    (wallpaper.category ?? '').toLowerCase() === selectedCategory.value.toLowerCase()
  );
});


const availableCategories = computed(() => {
  return categories.value.filter(category => category !== 'All');
});

// Methods
const loadCategories = async () => {
  try {
    const categoryList = await wallpaperService.getCategories();
    categories.value = ['All', ...categoryList];
  } catch (error) {
    console.error('Error loading categories:', error);
  }
};

const loadWallpapers = async () => {
  if (loading.value) return;
  
  loading.value = true;
  try {
    const result = await wallpaperService.getWallpapers({
      category: selectedCategory.value === 'All' ? undefined : selectedCategory.value,
      limitCount: 20
    });
    wallpapers.value = result.wallpapers;
    lastDoc.value = result.lastDoc;
    hasMore.value = result.wallpapers.length === 20;
  } catch (error) {
    console.error('Error loading wallpapers:', error);
  } finally {
    loading.value = false;
  }
};

const loadMoreWallpapers = async () => {
  if (loading.value || !hasMore.value) return;
  
  loading.value = true;
  try {
    const result = await wallpaperService.getWallpapers({
      category: selectedCategory.value === 'All' ? undefined : selectedCategory.value,
      limitCount: 20,
      lastDoc: lastDoc.value ?? undefined
    });
    wallpapers.value.push(...result.wallpapers);
    lastDoc.value = result.lastDoc;
    hasMore.value = result.wallpapers.length === 20;
  } catch (error) {
    console.error('Error loading more wallpapers:', error);
  } finally {
    loading.value = false;
  }
};

const loadUserFavorites = async () => {
  if (!currentUser.value) {
    userFavorites.value = [];
    return;
  }
  
  try {
    const favorites = await userService.getUserFavorites(currentUser.value.uid);
    userFavorites.value = favorites.map(fav => fav.wallpaperId);
  } catch (error) {
    console.error('Error loading user favorites:', error);
  }
};

const viewWallpaper = (wallpaper: Wallpaper) => {
  router.push(`/wallpaper/${wallpaper.id}`);
};

const handleCategorySelect = (category: string | null) => {
  selectedCategory.value = category || 'All';
};

// Lifecycle
onMounted(async () => {
  // Load initial data
  await Promise.all([
    loadCategories(),
    loadWallpapers()
  ]);
});

watch(currentUser, async () => {
  await loadUserFavorites();
}, { immediate: true });

// Watch for category changes
watch(selectedCategory, () => {
  // Reset pagination state when category changes
  lastDoc.value = null;
  hasMore.value = true;
  loadWallpapers();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _cache[2] || (_cache[2] = _createElementVNode("h1", { class: "text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-6 sm:mb-8" }, " Browse by Categories ", -1)),
    _createVNode(CategoryFilter, {
      categories: availableCategories.value,
      "selected-category": selectedCategory.value,
      loading: loading.value,
      onCategorySelect: handleCategorySelect
    }, null, 8, ["categories", "selected-category", "loading"]),
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("button", {
          onClick: _cache[0] || (_cache[0] = ($event: any) => (viewMode.value = 'grid')),
          class: _normalizeClass([
          'px-3 py-1 text-sm rounded-md transition-colors',
          viewMode.value === 'grid'
            ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
            : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
        ])
        }, " Grid ", 2),
        _createElementVNode("button", {
          onClick: _cache[1] || (_cache[1] = ($event: any) => (viewMode.value = 'preview')),
          class: _normalizeClass([
          'px-3 py-1 text-sm rounded-md transition-colors',
          viewMode.value === 'preview'
            ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
            : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
        ])
        }, " Device Preview ", 2)
      ])
    ]),
    _createVNode(GoogleAd, {
      "ad-type": "banner",
      class: "mb-6",
      "min-content-height": 400
    }),
    _createVNode(WallpaperGrid, {
      wallpapers: filteredWallpapers.value,
      loading: loading.value,
      "has-more": hasMore.value,
      "view-mode": viewMode.value,
      onWallpaperClick: viewWallpaper,
      onLoadMore: loadMoreWallpapers
    }, null, 8, ["wallpapers", "loading", "has-more", "view-mode"]),
    (filteredWallpapers.value.length > 6)
      ? (_openBlock(), _createBlock(GoogleAd, {
          key: 0,
          "ad-type": "rectangle",
          class: "mt-8 flex justify-center",
          "min-content-height": 800
        }))
      : _createCommentVNode("", true)
  ]))
}
}

})